import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useCategoriasWithFallback } from "../../../hooks/useCategorias";
import { useProductosByCategoria } from "../../../hooks/useProductos";
import { useNavigate } from "react-router-dom";
import { nombreToSlug } from "../../../utils/slugUtils";
import { useCarrito } from "../../../context/CarritoContext";
import CarritoModal from "../../Common/CarritoModal/CarritoModal";

const Productos = () => {
  const { categorias, loading: categoriasLoading } =
    useCategoriasWithFallback();
  const {
    productosByCategoria,
    loading: productosLoading,
    error,
  } = useProductosByCategoria();
  const navigate = useNavigate();
  const { addItem } = useCarrito();
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const loading = categoriasLoading || productosLoading;

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setSelectedQuantity(1);
  };

  if (loading) {
    return (
      <ProductosSection>
        <Container>
          <LoadingMessage>Cargando productos...</LoadingMessage>
        </Container>
      </ProductosSection>
    );
  }

  if (error) {
    return (
      <ProductosSection>
        <Container>
          <ErrorMessage>Error al cargar productos: {error}</ErrorMessage>
        </Container>
      </ProductosSection>
    );
  }

  if (categorias.length === 0) {
    return (
      <ProductosSection>
        <Container>
          <ErrorMessage>
            No hay categorías disponibles. Configura categorías en Strapi.
          </ErrorMessage>
        </Container>
      </ProductosSection>
    );
  }

  return (
    <ProductosSection id="productos-section">
      <Container>
        {categorias.map((categoria, index) => {
          const categoriaSlug = categoria.slug;
          const productosDeCategoria =
            productosByCategoria[categoriaSlug]?.productos || [];

          // No renderizar nada si la categoría no tiene productos
          if (productosDeCategoria.length === 0) {
            return null;
          }

          const mostrarBotones = productosDeCategoria.length > 4; // Desktop: más de 4
          const mostrarCarruselMobile = productosDeCategoria.length > 1; // Mobile: más de 1

          return (
            <CategoriaSection
              key={categoria.id}
              id={`categoria-${categoria.slug}`}
              as={motion.section}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.6,
                ease: "easeOut",
                delay: index * 0.1,
              }}
            >
              <CategoriaHeader>
                <CategoriaIconContainer>
                  {categoria.icono ? (
                    <CategoriaIcon
                      src={categoria.icono}
                      alt={categoria.nombre}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <CategoriaIconPlaceholder>📦</CategoriaIconPlaceholder>
                  )}
                </CategoriaIconContainer>
                <CategoriaTitle>{categoria.nombre}</CategoriaTitle>
              </CategoriaHeader>

              <CarruselContainer>
                {(mostrarBotones || mostrarCarruselMobile) && (
                  <CarruselButton
                    $position="left"
                    $esCarruselDesktop={mostrarBotones}
                    onClick={(e) => {
                      const carrusel =
                        e.currentTarget.parentElement.querySelector(
                          "[data-carrusel]"
                        );
                      if (carrusel) {
                        const scrollAmount = carrusel.offsetWidth * 0.8;
                        carrusel.scrollBy({
                          left: -scrollAmount,
                          behavior: "smooth",
                        });
                      }
                    }}
                    aria-label="Anterior"
                  >
                    <svg viewBox="0 0 20 20" fill="none">
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </CarruselButton>
                )}

                <ProductosCarrusel
                  data-carrusel
                  $mostrarScroll={mostrarBotones}
                  $mostrarCarruselMobile={mostrarCarruselMobile}
                >
                  {productosDeCategoria.map((producto) => (
                    <ProductoCard
                      key={producto.id}
                      onClick={() => {
                        const slug = nombreToSlug(producto.nombre);
                        navigate(`/catalogo/${slug}`, {
                          state: {
                            producto: {
                              id: producto.id,
                              nombre: producto.nombre,
                              descripcion: producto.descripcion,
                              talle: producto.talle,
                              color: producto.color,
                              imagen: producto.imagen,
                              precio: producto.precio,
                              categoriaId: producto.categoriaId
                            }
                          }
                        });
                      }}
                      role="button"
                      aria-label={`Ver ${producto.nombre}`}
                    >
                      <ProductoImage>
                        {producto.imagen ? (
                          <ProductoImageSrc
                            src={producto.imagen}
                            alt={producto.nombre}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <ImagePlaceholder>🛍️</ImagePlaceholder>
                        )}
                      </ProductoImage>
                      <ProductoInfo>
                        <ProductoNombre>{producto.nombre}</ProductoNombre>
                        <ProductoDescripcion>
                          {producto.descripcion}
                        </ProductoDescripcion>
                        <ProductoDetalles>
                          {producto.talle && (
                            <ProductoDetalle>
                              Talle: {producto.talle}
                            </ProductoDetalle>
                          )}
                          {producto.color && (
                            <ProductoDetalle>
                              Color: {producto.color}
                            </ProductoDetalle>
                          )}
                        </ProductoDetalles>
                        <ProductoPrecio>
                          {producto.precio
                            ? `$${producto.precio}`
                            : "Consultar precio"}
                        </ProductoPrecio>
                        <VerProductoButton
                          onClick={(e) => {
                            e.stopPropagation();
                            const slug = nombreToSlug(producto.nombre);
                            navigate(`/catalogo/${slug}`, {
                              state: {
                                producto: {
                                  id: producto.id,
                                  nombre: producto.nombre,
                                  descripcion: producto.descripcion,
                                  talle: producto.talle,
                                  color: producto.color,
                                  imagen: producto.imagen,
                                  precio: producto.precio,
                                  categoriaId: producto.categoriaId
                                }
                              }
                            });
                          }}
                        >
                          Ver Producto
                          <ArrowIcon>→</ArrowIcon>
                        </VerProductoButton>
                      </ProductoInfo>
                    </ProductoCard>
                  ))}
                </ProductosCarrusel>

                {(mostrarBotones || mostrarCarruselMobile) && (
                  <CarruselButton
                    $position="right"
                    $esCarruselDesktop={mostrarBotones}
                    onClick={(e) => {
                      const carrusel =
                        e.currentTarget.parentElement.querySelector(
                          "[data-carrusel]"
                        );
                      if (carrusel) {
                        const scrollAmount = carrusel.offsetWidth * 0.8;
                        carrusel.scrollBy({
                          left: scrollAmount,
                          behavior: "smooth",
                        });
                      }
                    }}
                    aria-label="Siguiente"
                  >
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </CarruselButton>
                )}
              </CarruselContainer>
            </CategoriaSection>
          );
        })}
      </Container>

      <CarritoModal
        isOpen={showModal}
        onClose={handleCloseModal}
        producto={selectedProduct}
        cantidad={selectedQuantity}
      />
    </ProductosSection>
  );
};

export default Productos;

const ProductosSection = styled.section`
  padding: 4rem 2rem;
  background: #f9f8f3;

  @media (max-width: 370px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: var(--text-black);
  font-family: "Onest", sans-serif;
  font-size: 1.2rem;
  padding: 3rem 0;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #6b7280;
  font-family: "Onest", sans-serif;
  font-size: 1rem;
  padding: 3rem 0;
  font-style: italic;
`;

const CategoriaSection = styled.section`
  margin-bottom: 4rem;
  overflow: visible;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoriaHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  flex-direction: column;
  margin-bottom: 2rem;
  border-left: 3px solid var(--inmove-color);
  padding-left: 1.5rem;

  @media (max-width: 768px) {
    border-left-width: 3px;
    padding-left: 1rem;
  }

  @media (max-width: 480px) {
    border-left-width: 2px;
    padding-left: 0.75rem;
  }
`;

const CategoriaIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CategoriaIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const CategoriaIconPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  opacity: 0.6;
`;

const CategoriaTitle = styled.h2`
  font-family: "Onest", sans-serif;
  font-weight: 500;
  font-size: 2rem;
  color: var(--text-black);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const CarruselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
  }
`;

const ProductosCarrusel = styled.div`
  /* Carrusel mode cuando hay más de 4 productos en DESKTOP */
  ${(props) =>
    props.$mostrarScroll
      ? `
    display: flex;
    flex-wrap: nowrap;
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-behavior: smooth;
    gap: 2rem;
    width: 100%;
    padding: 1rem 0;
    
    /* Ocultar scrollbar completamente */
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
    
    /* Scroll suave con snap */
    scroll-snap-type: x mandatory;
    
    & > * {
      flex: 0 0 calc(25% - 1.5rem);
      min-width: 280px;
      max-width: calc(25% - 1.5rem);
      scroll-snap-align: start;
    }
    
    @media (max-width: 1200px) {
      gap: 1.5rem;
      
      & > * {
        flex: 0 0 calc(33.333% - 1rem);
        min-width: 250px;
        max-width: calc(33.333% - 1rem);
      }
    }
  `
      : `
    /* Grid mode cuando hay 4 o menos productos en DESKTOP */
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    justify-items: start;
    
    @media (max-width: 1400px) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
    
    @media (max-width: 1200px) {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 1.5rem;
    }
  `}

  /* En MOBILE siempre carrusel si hay más de 1 producto */
  @media (max-width: 768px) {
    display: ${(props) => (props.$mostrarCarruselMobile ? "flex" : "grid")};

    ${(props) =>
      props.$mostrarCarruselMobile &&
      `
      flex-wrap: nowrap;
      overflow-x: scroll;
      scroll-behavior: smooth;
      gap: 1.5rem;
      scrollbar-width: none;
      -ms-overflow-style: none;
      scroll-snap-type: x mandatory;
      
      &::-webkit-scrollbar {
        display: none;
      }
      
      & > * {
        flex: 0 0 calc(50% - 0.75rem);
        min-width: 200px;
        max-width: calc(50% - 0.75rem);
        scroll-snap-align: start;
      }
    `}

    ${(props) =>
      !props.$mostrarCarruselMobile &&
      `
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
    `}
  }

  @media (max-width: 480px) {
    ${(props) =>
      props.$mostrarCarruselMobile
        ? `
      display: flex;
      flex-wrap: nowrap;
      overflow-x: scroll;
      scroll-behavior: smooth;
      gap: 1rem;
      scrollbar-width: none;
      -ms-overflow-style: none;
      scroll-snap-type: x mandatory;
      
      &::-webkit-scrollbar {
        display: none;
      }
      
      & > * {
        flex: 0 0 calc(100% - 0.5rem);
        min-width: calc(100% - 0.5rem);
        max-width: calc(100% - 0.5rem);
        scroll-snap-align: start;
      }
    `
        : `
      grid-template-columns: 1fr;
      gap: 1rem;
    `}
  }
`;

const CarruselButton = styled.button`
  position: absolute;
  top: 185px; /* Centrado con la imagen de 370px (370px / 2 = 185px) */
  ${(props) => (props.$position === "left" ? "left: -25px;" : "right: -25px;")}
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  color: var(--inmove-color);
  opacity: 0.7;

  svg {
    width: 30px;
    height: 30px;
    transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  &:hover {
    opacity: 1;
    transform: translateY(-50%) scale(1.2);

    svg {
      transform: ${(props) =>
        props.$position === "left" ? "translateX(-3px)" : "translateX(3px)"};
    }
  }

  &:active {
    transform: translateY(-50%) scale(1);
    opacity: 0.8;

    svg {
      transform: ${(props) =>
        props.$position === "left" ? "translateX(-1px)" : "translateX(1px)"};
    }
  }

  /* En desktop: mostrar solo cuando hay más de 4 productos */
  @media (min-width: 769px) {
    display: ${(props) => (props.$esCarruselDesktop ? "flex" : "none")};
  }

  @media (max-width: 1400px) {
    ${(props) =>
      props.$position === "left" ? "left: -20px;" : "right: -20px;"}
  }

  @media (max-width: 1200px) {
    ${(props) =>
      props.$position === "left" ? "left: -15px;" : "right: -15px;"}
    width: 45px;
    height: 45px;

    svg {
      width: 26px;
      height: 26px;
    }
  }

  @media (max-width: 768px) {
    ${(props) => (props.$position === "left" ? "left: 5px;" : "right: 5px;")}
    width: 40px;
    height: 40px;
    /* Centrado en toda la card */
    top: 50%;
    display: flex; /* Siempre visible en mobile si el botón se renderiza */

    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (max-width: 480px) {
    ${(props) => (props.$position === "left" ? "left: 0;" : "right: 0;")}
    width: 35px;
    height: 35px;
    /* Centrado en toda la card */
    top: 50%;
    display: flex; /* Siempre visible en mobile si el botón se renderiza */

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const ProductoCard = styled.div`
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  width: 100%;
  max-width: 350px;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ProductoImage = styled.div`
  width: 100%;
  border-radius: 24px;
  height: 370px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    height: 150px;
  }
`;

const ProductoImageSrc = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  font-size: 3rem;
  opacity: 0.3;
`;

const ProductoInfo = styled.div`
  padding: 1.5rem 0rem;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: 485px) {
      text-align: center;
    }
`;

const ProductoNombre = styled.h3`
  font-family: "Onest", sans-serif;
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--text-black);
  margin: 0 0 0.5rem 0;
`;

const ProductoDescripcion = styled.p`
  font-family: "Onest", sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  color: #807d7e;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 2.8rem; /* 2 líneas aprox (1.4 line-height * 2) */
`;

const ProductoDetalles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const ProductoDetalle = styled.span`
  font-family: "Onest", sans-serif;
  font-weight: 400;
  font-size: 0.85rem;
  color: #6b7280;
`;

const ProductoPrecio = styled.div`
  color: var(--inmove-color);
  font-family: 'Onest', sans-serif;
  font-weight: 700;
  font-size: 1.125rem;
  margin-bottom: 1rem;
  background: #FFF0F5;
  border: 1.5px solid #FFD6E8;
  padding: 8px 16px;
  border-radius: 8px;
  display: inline-block;
  width: fit-content;
`;

const VerProductoButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: var(--inmove-color);
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'Onest', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(218, 95, 139, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ArrowIcon = styled.span`
  font-size: 1.25rem;
  color: white;
  transition: transform 0.3s ease;

  ${VerProductoButton}:hover & {
    transform: translateX(4px);
  }
`;
